import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Eye, Check } from '../components/Icons.jsx';

// Tutor-facing teaching aid for the Reading Lab (VR speed-reading).
// Each passage has the controlled pace (WPM), the "wrong" reading habit
// to surface, a coaching script the tutor reads, and three true/false/CT
// questions to fire at the student once the passage closes.
const passages = [
  {
    title: 'NHS workforce report',
    wpm: 360,
    text:
      'The NHS in England now employs over 1.4 million people, making it one of the largest employers in the world. Despite this scale, the latest workforce report highlights persistent gaps in nursing, general practice and emergency medicine. Vacancy rates among nurses crossed 11 per cent in mid-2024, and a third of GP partners report intentions to retire within five years. The government has announced a long-term workforce plan, but trade unions argue the projected uplift in training places cannot offset attrition without sustained pay restoration. Critics inside the BMA also point to ongoing reliance on locum and agency staff, which inflates costs without resolving the structural shortage.',
    bad_habit: 'Backtracking on every comma-separated clause.',
    coach:
      '"As you read this aloud silently, keep one finger sweeping at a steady 360 WPM. The moment your eye snaps backwards, name it — \'backtracked\' — and keep going. We\'re training the reflex, not the comprehension."',
    questions: [
      { q: 'The NHS in England employs more than 1.5 million people.', a: 'False', why: '"Over 1.4 million" — the cap given is 1.4m, not 1.5m+.' },
      { q: 'A third of GP partners say they plan to retire within five years.', a: 'True', why: 'Stated directly.' },
      { q: 'The government\'s workforce plan will eliminate the structural shortage.', a: "Can't tell", why: 'Critics doubt it, but the passage does not say whether the plan will succeed.' },
    ],
  },
  {
    title: 'Antibiotic stewardship',
    wpm: 320,
    text:
      'Antimicrobial resistance is one of the World Health Organization\'s top global health threats. UK hospitals have run antibiotic stewardship programmes since the early 2000s, encouraging clinicians to prescribe narrow-spectrum agents and to de-escalate broad-spectrum antibiotics once culture results return. A recent meta-analysis of 38 acute trusts found that adherence to stewardship guidelines reduced Clostridium difficile incidence by roughly 19 per cent, with no overall increase in 30-day mortality. The same review noted that staff turnover and locum cover frequently weakened compliance, particularly in out-of-hours admissions.',
    bad_habit: 'Sub-vocalising every long word ("Clostridium", "antimicrobial").',
    coach:
      '"Long Latin names trap sub-vocalisers. Train them to chunk it: \'C-diff\' replaces \'Clostridium difficile\' in their head. When the eye sees the word, the brain notes \'long bacterial name, irrelevant to inference\'."',
    questions: [
      { q: 'Adherence to stewardship guidelines reduced C-diff incidence by about 19%.', a: 'True', why: 'Stated.' },
      { q: 'Stewardship programmes increased 30-day mortality.', a: 'False', why: '"No overall increase in 30-day mortality."' },
      { q: 'Locum-heavy departments will always have lower stewardship compliance.', a: "Can't tell", why: 'Passage says compliance was weakened "frequently" — not universally / always.' },
    ],
  },
  {
    title: 'Telemedicine in primary care',
    wpm: 380,
    text:
      'Telemedicine adoption accelerated during the pandemic and has only partially receded. By 2024, roughly 28 per cent of GP appointments in England were conducted remotely, down from a peak of 48 per cent in mid-2020 but still well above the pre-pandemic figure of 14 per cent. Patient satisfaction scores with remote appointments vary by age — those over 65 are 18 percentage points less satisfied on average than those under 35. Practices that offer the patient a choice of format report the highest satisfaction overall.',
    bad_habit: 'Reading the numbers out one digit at a time.',
    coach:
      '"Numbers swallow your time. Train the student to glance the comparison: \'28 vs 14 = roughly doubled vs pre-pandemic\'. Don\'t hover. The exact percentages rarely matter for inference questions."',
    questions: [
      { q: 'Remote appointments doubled compared with the pre-pandemic figure.', a: 'True', why: '28% vs 14% = exactly doubled.' },
      { q: 'Over-65s are less satisfied with remote appointments on average.', a: 'True', why: 'Stated.' },
      { q: 'Offering patients a choice of format is the most popular factor with patients overall.', a: "Can't tell", why: 'Highest satisfaction ≠ most popular factor. Don\'t inflate phrasing.' },
    ],
  },
];

export default function TutorReadingLab() {
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState('brief'); // brief | passage | quiz
  const [revealed, setRevealed] = useState({});
  const p = passages[idx];

  const next = () => {
    setIdx((i) => (i + 1) % passages.length);
    setPhase('brief');
    setRevealed({});
  };

  return (
    <>
      <Link to="/tutor-portal" className="back-link"><ArrowLeft width="16" height="16" />Back to tutor portal</Link>
      <div className="page-head">
        <div className="kicker" style={{ color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: 12 }}>Tutor Portal · Reading Lab Live</div>
        <h1>Train the reading reflex, in the room.</h1>
        <p className="subtitle">Project this on a tutor screen. Each passage has a controlled pace, a coaching script you read out, and three true/false/can\'t-tell questions to fire once the passage closes.</p>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 18 }}>
        <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Passage {idx + 1} of {passages.length}</span>
        <span style={{ padding: '4px 10px', borderRadius: 999, background: '#f3eefc', color: '#5b21b6', fontWeight: 700, fontSize: 12 }}>{p.wpm} WPM target</span>
        <button type="button" className="btn" style={{ marginLeft: 'auto' }} onClick={next}>
          Next passage <ArrowRight width="14" height="14" />
        </button>
      </div>

      {phase === 'brief' && (
        <div className="card" style={{ marginBottom: 22, padding: '24px 26px' }}>
          <h2 style={{ margin: '0 0 6px', fontSize: 18, color: '#5b21b6' }}>Coach brief — {p.title}</h2>
          <p style={{ margin: '0 0 12px', fontSize: 14, color: '#4b4361' }}>The bad habit to surface in this round:</p>
          <div style={{ background: '#fef3c7', borderLeft: '4px solid #d97706', padding: '12px 16px', borderRadius: 8, fontWeight: 600, color: '#7c2d12', marginBottom: 14 }}>
            {p.bad_habit}
          </div>
          <p style={{ margin: '0 0 6px', fontSize: 14, color: '#4b4361' }}>Read this aloud as the student starts:</p>
          <div style={{ background: '#f3eefc', borderLeft: '4px solid #5b21b6', padding: '12px 16px', borderRadius: 8, fontStyle: 'italic', color: '#1f1240', marginBottom: 18 }}>
            {p.coach}
          </div>
          <button type="button" className="btn btn-primary" onClick={() => setPhase('passage')}>
            <Eye width="14" height="14" /> Start passage
          </button>
        </div>
      )}

      {phase === 'passage' && (
        <div className="card" style={{ marginBottom: 22, padding: '28px 30px', background: '#fff8f0', borderLeft: '4px solid #f97316' }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 18, color: '#7c2d12' }}>Reading at {p.wpm} WPM — give them ~{Math.round((p.text.split(/\s+/).length / p.wpm) * 60)} seconds</h2>
          <p style={{ fontSize: 16, lineHeight: 1.65, color: '#1f1240' }}>{p.text}</p>
          <button type="button" className="btn btn-primary" style={{ marginTop: 14 }} onClick={() => setPhase('quiz')}>
            Close passage → fire questions
          </button>
        </div>
      )}

      {phase === 'quiz' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {p.questions.map((q, qi) => (
            <div key={qi} className="card" style={{ padding: '18px 22px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#5b21b6', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Q{qi + 1}</div>
              <p style={{ fontSize: 15, color: '#1f1240', margin: '0 0 12px' }}>{q.q}</p>
              {!revealed[qi] && (
                <button type="button" className="btn" onClick={() => setRevealed({ ...revealed, [qi]: true })}>
                  <Check width="14" height="14" /> Reveal answer + rationale
                </button>
              )}
              {revealed[qi] && (
                <div style={{ background: '#dcfce7', borderLeft: '4px solid #16a34a', padding: '10px 14px', borderRadius: 8 }}>
                  <strong style={{ color: '#166534' }}>{q.a}</strong>
                  <div style={{ fontSize: 13.5, color: '#1f1240', marginTop: 4 }}>{q.why}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
